/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  addDecoderSizePrefix,
  addEncoderSizePrefix,
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getAddressEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getOptionDecoder,
  getOptionEncoder,
  getProgramDerivedAddress,
  getStructDecoder,
  getStructEncoder,
  getU32Decoder,
  getU32Encoder,
  getUtf8Decoder,
  getUtf8Encoder,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type Codec,
  type Decoder,
  type Encoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type Option,
  type OptionOrNullable,
  type ReadonlyAccount,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
  type WritableSignerAccount,
} from "@solana/kit";
import { GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS } from "../programs";
import {
  expectAddress,
  getAccountMetaFactory,
  type ResolvedAccount,
} from "../shared";
import {
  getServiceEndpointDecoder,
  getServiceEndpointEncoder,
  getVerificationMethodDecoder,
  getVerificationMethodEncoder,
  type ServiceEndpoint,
  type ServiceEndpointArgs,
  type VerificationMethod,
  type VerificationMethodArgs,
} from "../types";

export const UPDATE_DID_DOCUMENT_DISCRIMINATOR = new Uint8Array([
  64, 216, 17, 91, 205, 94, 45, 57,
]);

export function getUpdateDidDocumentDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    UPDATE_DID_DOCUMENT_DISCRIMINATOR,
  );
}

export type UpdateDidDocumentInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountDidDocument extends string | AccountMeta<string> = string,
  TAccountController extends string | AccountMeta<string> = string,
  TAccountClock extends string | AccountMeta<string> =
    "SysvarC1ock11111111111111111111111111111111",
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountDidDocument extends string
        ? WritableAccount<TAccountDidDocument>
        : TAccountDidDocument,
      TAccountController extends string
        ? WritableSignerAccount<TAccountController> &
            AccountSignerMeta<TAccountController>
        : TAccountController,
      TAccountClock extends string
        ? ReadonlyAccount<TAccountClock>
        : TAccountClock,
      ...TRemainingAccounts,
    ]
  >;

export type UpdateDidDocumentInstructionData = {
  discriminator: ReadonlyUint8Array;
  addVerificationMethod: Option<VerificationMethod>;
  removeVerificationMethodId: Option<string>;
  addServiceEndpoint: Option<ServiceEndpoint>;
  removeServiceEndpointId: Option<string>;
};

export type UpdateDidDocumentInstructionDataArgs = {
  addVerificationMethod: OptionOrNullable<VerificationMethodArgs>;
  removeVerificationMethodId: OptionOrNullable<string>;
  addServiceEndpoint: OptionOrNullable<ServiceEndpointArgs>;
  removeServiceEndpointId: OptionOrNullable<string>;
};

export function getUpdateDidDocumentInstructionDataEncoder(): Encoder<UpdateDidDocumentInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ["discriminator", fixEncoderSize(getBytesEncoder(), 8)],
      [
        "addVerificationMethod",
        getOptionEncoder(getVerificationMethodEncoder()),
      ],
      [
        "removeVerificationMethodId",
        getOptionEncoder(
          addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder()),
        ),
      ],
      ["addServiceEndpoint", getOptionEncoder(getServiceEndpointEncoder())],
      [
        "removeServiceEndpointId",
        getOptionEncoder(
          addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder()),
        ),
      ],
    ]),
    (value) => ({ ...value, discriminator: UPDATE_DID_DOCUMENT_DISCRIMINATOR }),
  );
}

export function getUpdateDidDocumentInstructionDataDecoder(): Decoder<UpdateDidDocumentInstructionData> {
  return getStructDecoder([
    ["discriminator", fixDecoderSize(getBytesDecoder(), 8)],
    ["addVerificationMethod", getOptionDecoder(getVerificationMethodDecoder())],
    [
      "removeVerificationMethodId",
      getOptionDecoder(addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder())),
    ],
    ["addServiceEndpoint", getOptionDecoder(getServiceEndpointDecoder())],
    [
      "removeServiceEndpointId",
      getOptionDecoder(addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder())),
    ],
  ]);
}

export function getUpdateDidDocumentInstructionDataCodec(): Codec<
  UpdateDidDocumentInstructionDataArgs,
  UpdateDidDocumentInstructionData
> {
  return combineCodec(
    getUpdateDidDocumentInstructionDataEncoder(),
    getUpdateDidDocumentInstructionDataDecoder(),
  );
}

export type UpdateDidDocumentAsyncInput<
  TAccountDidDocument extends string = string,
  TAccountController extends string = string,
  TAccountClock extends string = string,
> = {
  /** DID document account with canonical PDA validation */
  didDocument?: Address<TAccountDidDocument>;
  /** Controller of the DID (must match document controller) */
  controller: TransactionSigner<TAccountController>;
  /** Clock sysvar for timestamp validation */
  clock?: Address<TAccountClock>;
  addVerificationMethod: UpdateDidDocumentInstructionDataArgs["addVerificationMethod"];
  removeVerificationMethodId: UpdateDidDocumentInstructionDataArgs["removeVerificationMethodId"];
  addServiceEndpoint: UpdateDidDocumentInstructionDataArgs["addServiceEndpoint"];
  removeServiceEndpointId: UpdateDidDocumentInstructionDataArgs["removeServiceEndpointId"];
};

export async function getUpdateDidDocumentInstructionAsync<
  TAccountDidDocument extends string,
  TAccountController extends string,
  TAccountClock extends string,
  TProgramAddress extends Address =
    typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
>(
  input: UpdateDidDocumentAsyncInput<
    TAccountDidDocument,
    TAccountController,
    TAccountClock
  >,
  config?: { programAddress?: TProgramAddress },
): Promise<
  UpdateDidDocumentInstruction<
    TProgramAddress,
    TAccountDidDocument,
    TAccountController,
    TAccountClock
  >
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    didDocument: { value: input.didDocument ?? null, isWritable: true },
    controller: { value: input.controller ?? null, isWritable: true },
    clock: { value: input.clock ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.didDocument.value) {
    accounts.didDocument.value = await getProgramDerivedAddress({
      programAddress,
      seeds: [
        getBytesEncoder().encode(
          new Uint8Array([
            100, 105, 100, 95, 100, 111, 99, 117, 109, 101, 110, 116,
          ]),
        ),
        getAddressEncoder().encode(expectAddress(accounts.controller.value)),
      ],
    });
  }
  if (!accounts.clock.value) {
    accounts.clock.value =
      "SysvarC1ock11111111111111111111111111111111" as Address<"SysvarC1ock11111111111111111111111111111111">;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.didDocument),
      getAccountMeta(accounts.controller),
      getAccountMeta(accounts.clock),
    ],
    data: getUpdateDidDocumentInstructionDataEncoder().encode(
      args as UpdateDidDocumentInstructionDataArgs,
    ),
    programAddress,
  } as UpdateDidDocumentInstruction<
    TProgramAddress,
    TAccountDidDocument,
    TAccountController,
    TAccountClock
  >);
}

export type UpdateDidDocumentInput<
  TAccountDidDocument extends string = string,
  TAccountController extends string = string,
  TAccountClock extends string = string,
> = {
  /** DID document account with canonical PDA validation */
  didDocument: Address<TAccountDidDocument>;
  /** Controller of the DID (must match document controller) */
  controller: TransactionSigner<TAccountController>;
  /** Clock sysvar for timestamp validation */
  clock?: Address<TAccountClock>;
  addVerificationMethod: UpdateDidDocumentInstructionDataArgs["addVerificationMethod"];
  removeVerificationMethodId: UpdateDidDocumentInstructionDataArgs["removeVerificationMethodId"];
  addServiceEndpoint: UpdateDidDocumentInstructionDataArgs["addServiceEndpoint"];
  removeServiceEndpointId: UpdateDidDocumentInstructionDataArgs["removeServiceEndpointId"];
};

export function getUpdateDidDocumentInstruction<
  TAccountDidDocument extends string,
  TAccountController extends string,
  TAccountClock extends string,
  TProgramAddress extends Address =
    typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
>(
  input: UpdateDidDocumentInput<
    TAccountDidDocument,
    TAccountController,
    TAccountClock
  >,
  config?: { programAddress?: TProgramAddress },
): UpdateDidDocumentInstruction<
  TProgramAddress,
  TAccountDidDocument,
  TAccountController,
  TAccountClock
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    didDocument: { value: input.didDocument ?? null, isWritable: true },
    controller: { value: input.controller ?? null, isWritable: true },
    clock: { value: input.clock ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.clock.value) {
    accounts.clock.value =
      "SysvarC1ock11111111111111111111111111111111" as Address<"SysvarC1ock11111111111111111111111111111111">;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.didDocument),
      getAccountMeta(accounts.controller),
      getAccountMeta(accounts.clock),
    ],
    data: getUpdateDidDocumentInstructionDataEncoder().encode(
      args as UpdateDidDocumentInstructionDataArgs,
    ),
    programAddress,
  } as UpdateDidDocumentInstruction<
    TProgramAddress,
    TAccountDidDocument,
    TAccountController,
    TAccountClock
  >);
}

export type ParsedUpdateDidDocumentInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    /** DID document account with canonical PDA validation */
    didDocument: TAccountMetas[0];
    /** Controller of the DID (must match document controller) */
    controller: TAccountMetas[1];
    /** Clock sysvar for timestamp validation */
    clock: TAccountMetas[2];
  };
  data: UpdateDidDocumentInstructionData;
};

export function parseUpdateDidDocumentInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedUpdateDidDocumentInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 3) {
    // TODO: Coded error.
    throw new Error("Not enough accounts");
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      didDocument: getNextAccount(),
      controller: getNextAccount(),
      clock: getNextAccount(),
    },
    data: getUpdateDidDocumentInstructionDataDecoder().decode(instruction.data),
  };
}
